import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useStrings } from '../i18n';

const SOCIAL = [
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
  },
];

const NavLink = ({ onClick, children }) => (
  <li>
    <button
      onClick={onClick}
      className="text-left text-sm md:text-base text-bodyText hover:text-secondary cursor-pointer transition bg-transparent border-0 p-0"
    >
      {children}
    </button>
  </li>
);

export default function Footer() {
  const navigate = useNavigate();
  const strings = useStrings();

  const exploreLinks = [
    { label: 'Home',          path: '/' },
    { label: 'The Elements',  path: '/shop' },
    { label: 'Our Philosophy',path: '/about' },
    { label: 'Shop',          path: '/shop' },
  ];

  const policyLinks = [
    { label: 'Terms of Service', path: '/terms' },
    { label: 'Privacy Policy',   path: '/privacy' },
    { label: 'Return Policy',    path: '/returns' },
    { label: 'Shipping Policy',  path: '/shipping' },
  ];

  const supportLinks = [
    { label: 'FAQ',             path: '/faq' },
    { label: 'Contact Us',      path: '/contact' },
    { label: 'Track Order',     path: '/orders' },
    { label: 'Help & Support',  path: '/help' },
  ];

  return (
    <footer className="bg-primaryBg text-bodyText py-8 md:py-12 mt-12 md:mt-20 shadow-inner">
      <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">

        {/* Brand */}
        <div className="col-span-2 md:col-span-1">
          <h2 className="text-base md:text-xl text-headingText font-heading mb-3 md:mb-4">
            {strings.footer.brand}
          </h2>
          <p className="text-sm md:text-base text-bodyText mb-5">{strings.footer.tagline}</p>

          {/* Social */}
          <div className="flex gap-3">
            {SOCIAL.map((s) => (
              <a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.name}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-cardBg text-bodyText hover:text-secondary hover:bg-secondary/10 transition"
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>

        {/* Explore */}
        <div>
          <h2 className="text-base md:text-xl text-headingText font-heading mb-3 md:mb-4">
            {strings.footer.exploreTitle}
          </h2>
          <ul className="space-y-1.5 md:space-y-2">
            {exploreLinks.map((link) => (
              <NavLink key={link.label} onClick={() => navigate(link.path)}>
                {link.label}
              </NavLink>
            ))}
          </ul>
        </div>

        {/* Policies */}
        <div>
          <h2 className="text-base md:text-xl text-headingText font-heading mb-3 md:mb-4">Policies</h2>
          <ul className="space-y-1.5 md:space-y-2">
            {policyLinks.map((link) => (
              <NavLink key={link.label} onClick={() => navigate(link.path)}>
                {link.label}
              </NavLink>
            ))}
          </ul>
        </div>

        {/* Support */}
        <div>
          <h2 className="text-base md:text-xl text-headingText font-heading mb-3 md:mb-4">Support</h2>
          <ul className="space-y-1.5 md:space-y-2">
            {supportLinks.map((link) => (
              <NavLink key={link.label} onClick={() => navigate(link.path)}>
                {link.label}
              </NavLink>
            ))}
          </ul>

          {/* Contact quick info */}
          <div className="mt-5 space-y-1.5">
            <a
              href={`mailto:${strings.footer.contact.email}`}
              className="flex items-center gap-1.5 text-xs md:text-sm text-bodyText hover:text-secondary transition"
            >
              <span>✉️</span> {strings.footer.contact.email}
            </a>
            <a
              href={`tel:${strings.footer.contact.phone.replace(/\s/g, '')}`}
              className="flex items-center gap-1.5 text-xs md:text-sm text-bodyText hover:text-secondary transition"
            >
              <span>📞</span> {strings.footer.contact.phone}
            </a>
          </div>
        </div>

      </div>

      {/* Legal / Ownership */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 mt-8 md:mt-12 pt-6 border-t border-gray-200 text-xs md:text-sm text-bodyText text-center space-y-1 md:space-y-2">
        {strings.footer.legal.map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>
    </footer>
  );
}

